import { useCallback, useEffect, useState } from 'react';
import { BeakerIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type BirdGenotype } from '../../api/genetics';
import { Gender } from '../../utils/enums';
import { allelesToStatus, getLocusStatusOptions } from '../../utils/genotypeEditorUtils';
import type { Bird } from '../../api/birds';

interface BirdGeneticsPanelProps {
  bird: Bird;
  onImported?: () => void;
}

export default function BirdGeneticsPanel({ bird, onImported }: BirdGeneticsPanelProps) {
  const [genotypes, setGenotypes] = useState<BirdGenotype[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  const speciesId = bird.species?.id || bird.speciesId;

  const loadGenotypes = useCallback(async () => {
    if (!bird.id) return;
    setLoading(true);
    setError(null);
    try {
      const records = await geneticsApi.getBirdGenotypes(bird.id);
      setGenotypes(records);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load genotypes');
    } finally {
      setLoading(false);
    }
  }, [bird.id]);

  useEffect(() => {
    loadGenotypes();
  }, [loadGenotypes]);

  const handleImportLegacy = async () => {
    setImporting(true);
    setImportMessage(null);
    setError(null);
    try {
      const result = await geneticsApi.importLegacyGenotypes(bird.id);
      if (result.imported > 0) {
        setImportMessage(`Imported ${result.imported} locus(es): ${result.locusSymbols.join(', ')}`);
        await loadGenotypes();
        onImported?.();
      } else {
        setImportMessage('No matching loci found in legacy text fields.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const hasLegacyText = !!(bird.genotype?.trim() || bird.mutationName?.trim() || bird.phenotype?.trim());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BeakerIcon className="h-5 w-5 text-indigo-600" />
            Genetics
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Structured genotypes drive breeding predictions. Edit the bird to change locus status.
          </p>
        </div>
        {hasLegacyText && (
          <button
            type="button"
            onClick={handleImportLegacy}
            disabled={importing}
            className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            {importing ? 'Importing…' : 'Import from legacy text'}
          </button>
        )}
      </div>

      {importMessage && (
        <p className="text-sm text-indigo-800 bg-indigo-50 border border-indigo-100 rounded px-3 py-2">
          {importMessage}
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {(bird.mutationName || bird.phenotype || bird.genotype) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {bird.mutationName && (
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
              <dt className="text-xs font-medium text-purple-700">Mutation name</dt>
              <dd className="mt-1 text-sm text-purple-900">{bird.mutationName}</dd>
            </div>
          )}
          {bird.phenotype && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-3">
              <dt className="text-xs font-medium text-green-700">Phenotype</dt>
              <dd className="mt-1 text-sm text-green-900">{bird.phenotype}</dd>
            </div>
          )}
          {bird.genotype && (
            <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3">
              <dt className="text-xs font-medium text-cyan-700">Genotype (legacy)</dt>
              <dd className="mt-1 text-sm text-cyan-900 font-mono text-xs">{bird.genotype}</dd>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading structured genotypes…</p>
      ) : genotypes.length === 0 ? (
        <p className="text-sm text-slate-500">
          No structured genotypes recorded.
          {hasLegacyText ? ' Use "Import from legacy text" or edit the bird to set loci.' : ' Edit the bird to record genotypes.'}
        </p>
      ) : (
        <div className="space-y-2">
          {genotypes.map((record) => {
            const locus = record.locus;
            if (!locus) return null;
            const status = allelesToStatus(locus, record.alleles, bird.gender || Gender.UNKNOWN);
            const options = getLocusStatusOptions(locus, bird.gender || Gender.UNKNOWN);
            const label = options.find((o) => o.value === status)?.label ?? status;
            return (
              <div key={record.id} className="rounded-md border border-slate-200 bg-white p-3 flex justify-between gap-3">
                <div>
                  <span className="font-medium text-slate-900">{locus.locusName}</span>
                  <span className="ml-2 font-mono text-xs text-slate-500">({locus.locusSymbol})</span>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">{record.alleles.join('/')}</p>
                </div>
                <div className="text-right text-xs">
                  <span className={`inline-flex px-2 py-0.5 rounded font-medium ${
                    record.isVisual ? 'bg-purple-100 text-purple-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {record.isVisual ? 'Visual' : record.isSplit ? 'Split' : record.zygosity}
                  </span>
                  <p className="text-slate-500 mt-1 max-w-[12rem] truncate" title={label}>{label}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
